import { apiClient } from '../config';
import { endpoints } from '../endpoints';

/**
 * Issue service for handling issue operations
 */
export const issueService = {
  /**
   * Get all issues for a journal
   * @param {string} journalId - Journal ID
   * @returns {Promise<Array>} - Promise resolving to array of issues
   */
  getIssuesByJournal: async (journalId) => {
    const response = await apiClient.get(endpoints.issues.getByJournal(journalId));
    return response.data;
  },

  /**
   * Get all issues for a volume
   * @param {string} volumeId - Volume ID
   * @returns {Promise<Array>} - Promise resolving to array of issues
   */
  getIssuesByVolume: async (volumeId) => {
    const response = await apiClient.get(endpoints.issues.getByVolume(volumeId));
    return response.data;
  },

  /**
   * Get issue by ID
   * @param {string} id - Issue ID
   * @returns {Promise<Object>} - Promise resolving to issue data
   */
  getIssueById: async (id) => {
    const response = await apiClient.get(endpoints.issues.getById(id));
    return response.data.data;
  },

  /**
   * Create a new issue
   * @param {string} journalId - Journal ID
   * @param {string} volumeId - Volume ID
   * @param {Object} issueData - Issue data
   * @param {number} issueData.number - Issue number
   * @param {number} issueData.year - Issue year
   * @param {string} issueData.title - Issue title
   * @param {string} issueData.description - Issue description
   * @param {string} issueData.status - Issue status
   * @returns {Promise<Object>} - Promise resolving to created issue
   */
  createIssue: async (journalId, volumeId, issueData) => {
    const response = await apiClient.post(endpoints.issues.create(journalId, volumeId), issueData);
    return response.data.data;
  },

  /**
   * Update an existing issue
   * @param {string} id - Issue ID
   * @param {Object} issueData - Issue data to update
   * @param {number} [issueData.number] - Issue number
   * @param {number} [issueData.year] - Issue year
   * @param {string} [issueData.title] - Issue title
   * @param {string} [issueData.description] - Issue description
   * @param {string} [issueData.status] - Issue status
   * @returns {Promise<Object>} - Promise resolving to updated issue
   */
  updateIssue: async (id, issueData) => {
    const response = await apiClient.put(endpoints.issues.update(id), issueData);
    return response.data.data;
  },

  /**
   * Delete an issue
   * @param {string} id - Issue ID
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  deleteIssue: async (id) => {
    const response = await apiClient.delete(endpoints.issues.delete(id));
    return response.data;
  },

  /**
   * Upload a file for an issue
   * @param {string} issueId - Issue ID
   * @param {File} file - File to upload
   * @returns {Promise<Object>} - Promise resolving to updated issue
   */
  uploadIssueFile: async (issueId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      endpoints.issues.uploadFile(issueId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  },

  /**
   * Get the download URL for an issue file
   * @param {string} issueId - Issue ID
   * @returns {string} - URL for downloading the issue file
   */
  getIssueFileDownloadUrl: (issueId) => {
    return endpoints.issues.downloadFile(issueId);
  }
};
